import {
  Divider,
  HStack,
  Heading,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { KYDTicketType } from "@common/interfaces/KYDTicket";
import { PriceMode, TicketTypeValues } from "./schema";

const TicketTypeSettingsSection = ({
  isLoading,
  waitlistStatus,
  waitlistReturn,
  eventLink,
}: {
  isLoading: boolean;
  waitlistStatus: KYDTicketType["waitlist_status"];
  waitlistReturn: KYDTicketType["waitlist_return"];
  eventLink?: string;
}) => {
  const {
    watch,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext<TicketTypeValues>();
  const hidden = watch("hidden");
  const priceMode = watch("price_mode");
  const tiers = watch("tiers");
  const isMovesTickets = watch("shift_quantity_on_expiration");
  const _waitlistStatus = watch("waitlist_status", waitlistStatus);
  const _waitlistReturn = watch("waitlist_return", waitlistReturn);

  const increment = watch("increment");

  const [isCodeOpen, setIsCodeOpen] = useState(hidden);

  return (
    <Stack spacing={2} w="full">
      <Stack>
        <Heading size="md">Settings</Heading>
        <Divider />
      </Stack>
      <HStack w="full" gap={4}>
        <Switch
          colorScheme="green"
          defaultChecked={
            _waitlistStatus === "OPEN" || _waitlistStatus === "ENABLED"
          }
          isDisabled={increment > 1 || isLoading}
          onChange={() => {
            const changingTo =
              !_waitlistStatus || _waitlistStatus === "DISABLED"
                ? "ENABLED"
                : "DISABLED";
            console.log("Waitlist Status", changingTo);
            setValue("waitlist_status", changingTo, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          ml={2}
        />
        <VStack w="full" alignItems={"flex-start"} gap={0}>
          <Text fontWeight={"medium"}>Enable waitlist</Text>
          <Text fontSize={10} color="gray.500">
            Allow fans to join a waitlist for sold-out events. Increase your
            chances of a full house by enabling this feature.
          </Text>
        </VStack>
      </HStack>
      <HStack w="full" gap={4}>
        <Switch
          colorScheme="green"
          defaultChecked={_waitlistReturn === "ENABLED"}
          isDisabled={increment > 1 || isLoading}
          onChange={() => {
            const changingTo =
              !_waitlistReturn || _waitlistReturn === "DISABLED"
                ? "ENABLED"
                : "DISABLED";
            console.log("Fan Waitlist Return", changingTo);
            setValue("waitlist_return", changingTo, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          ml={2}
        />
        <VStack w="full" alignItems={"flex-start"} gap={0}>
          <Text fontWeight={"medium"}>Enable waitlist returns</Text>
          <Text fontSize={10} color="gray.500">
            Allow fans to sell tickets to each other on the waitlist.
          </Text>
        </VStack>
      </HStack>
      {priceMode === PriceMode.TIERED_ALLOCATION ? (
        <>
          <HStack w="full" gap={4}>
            <Switch
              colorScheme="green"
              defaultChecked={isMovesTickets}
              isDisabled={isLoading}
              onChange={() => {
                setValue("shift_quantity_on_expiration", !isMovesTickets, {
                  shouldDirty: true,
                });

                tiers.forEach((t, index) => {
                  // do not set on the last
                  if (index === tiers.length - 1) return;

                  setValue(
                    `tiers.${index}.shift_quantity_on_expiration`,
                    !isMovesTickets,
                    {
                      shouldDirty: true,
                    }
                  );
                });
                trigger("tiers");
              }}
              ml={2}
            />
            <Text fontWeight={"medium"}>
              Move tickets to next tier on expiry date or refund
            </Text>
          </HStack>
        </>
      ) : null}
      {priceMode === PriceMode.SINGLE ? (
        <>
          <HStack w="full" justifyContent={"space-between"}>
            <HStack spacing={4}>
              <Switch
                colorScheme="green"
                defaultChecked={isCodeOpen}
                isDisabled={isLoading}
                onChange={() => {
                  const changingTo = !isCodeOpen;
                  setIsCodeOpen(changingTo);
                  setValue("hidden", changingTo, {
                    shouldDirty: true,
                  });

                  if (changingTo === false) {
                    setValue("code", null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                }}
                ml={2}
              />
              <Text fontWeight={"medium"}>
                Require code to unlock ticket type
              </Text>
            </HStack>
          </HStack>
        </>
      ) : null}
    </Stack>
  );
};

export default TicketTypeSettingsSection;
